import { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { Tooltip } from 'react-tooltip';
import Plotly from 'plotly.js-dist';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import './HealthDataVisualizer.css';

const getSelectionDisplayText = (selected, options) => {
  if (selected.length === 0) return 'None Selected';
  if (selected.length === options.length && options.length > 0) return 'All Selected';
  if (selected.length === 1) return selected[0];
  return `${selected.length} Selected`;
};

const SearchableMultiSelect = ({ options, selected, onToggle, onSelectAll, onDeselectAll, placeholder, id, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div className="dropdown-header" onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{ opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={disabled ? placeholder : getSelectionDisplayText(selected, options)}
          onClick={(e) => { e.stopPropagation(); if (!disabled) setIsOpen(true); }}
          className="dropdown-search-input" disabled={disabled} />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-menu">
          <div className="dropdown-actions">
            <button className="dropdown-action-button" onClick={onSelectAll}>Select All</button>
            <button className="dropdown-action-button" onClick={onDeselectAll}>Deselect All</button>
          </div>
          {filteredOptions.length > 0 ? filteredOptions.map((opt, index) => (
            <div key={index} className={`dropdown-item ${selected.includes(opt) ? 'selected' : ''}`} onClick={() => onToggle(opt)}>
              <input type="checkbox" checked={selected.includes(opt)} readOnly />
              <span>{opt}</span>
            </div>
          )) : <div className="dropdown-item no-options">No options found</div>}
        </div>
      )}
    </div>
  );
};

const Assessment = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Track if initial data load is complete - use state so React tracks it properly
  const [dataLoaded, setDataLoaded] = useState(false);

  const [dataStructure, setDataStructure] = useState({ countries: [], domains: [], components: [], subcomponents: [], elements: [] });

  const [availableDomains, setAvailableDomains] = useState([]);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [availableSubcomponents, setAvailableSubcomponents] = useState([]);
  const [availableElements, setAvailableElements] = useState([]);

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [selectedSubcomponents, setSelectedSubcomponents] = useState([]);
  const [selectedElements, setSelectedElements] = useState([]);

  const [showLegend, setShowLegend] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showData, setShowData] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const chartRef = useRef(null);
  const contentAreaRef = useRef(null);

  const colorPalette = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'];

  const exportToPNG = async () => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;
    try {
      const canvas = await html2canvas(contentArea, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: true });
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.font = 'italic 22px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`© Health Systems Innovation Lab, Harvard University, ${new Date().getFullYear()}`, 40, canvas.height - 30);
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoHeight = 50;
        const logoWidth = logoHeight * (logo.width / logo.height);
        ctx.drawImage(logo, finalCanvas.width - logoWidth - 40, canvas.height - logoHeight - 20, logoWidth, logoHeight);
        finalCanvas.toBlob(blob => saveAs(blob, `t1d_assessment_${new Date().toISOString().split('T')[0]}.png`));
      };
      logo.onerror = () => finalCanvas.toBlob(blob => saveAs(blob, `t1d_assessment_${new Date().toISOString().split('T')[0]}.png`));
      logo.src = '/logo.png';
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Failed to export image.');
    }
  };

  // Load data with RANDOM initial selection
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const csvResponse = await fetch('/Current/t1dataset_assesment.csv');
        if (!csvResponse.ok) throw new Error(`HTTP error: ${csvResponse.status}`);
        const csvText = await csvResponse.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: false,
          skipEmptyLines: true,
          complete: results => {
            const cleanedData = results.data
              .filter(row => row.Country && row.Elements && row['Element Score (%)'] !== undefined && row['Element Score (%)'] !== '')
              .map(row => ({
                Country: (row.Country || '').trim(),
                Domain: (row.Domain || '').trim(),
                DomainDefinition: (row['Domain Definition'] || '').trim(),
                Component: (row.Component || '').trim(),
                ComponentDefinition: (row['Component  Defintion'] || '').trim(),
                Subcomponent: (row['Subcomponent '] || row.Subcomponent || '').trim(),
                SubcomponentDefinition: (row['Subcomponent definition'] || '').trim(),
                Elements: (row.Elements || '').trim(),
                ElementDefinition: (row['Element Definition'] || '').trim(),
                ElementScore: parseFloat(row['Element Score (%)']) || 0,
                ElementRawScore: parseFloat(row['Element Raw Score '] || row['Element Raw Score']) || 0,
                TotalClinics: parseInt(row['Total Clinics Within Country']) || 0
              }))
              .filter(row => row.Country && row.Elements);

            const structure = {
              countries: [...new Set(cleanedData.map(row => row.Country))].filter(Boolean).sort(),
              domains: [...new Set(cleanedData.map(row => row.Domain))].filter(Boolean).sort(),
              components: [...new Set(cleanedData.map(row => row.Component))].filter(Boolean).sort(),
              subcomponents: [...new Set(cleanedData.map(row => row.Subcomponent))].filter(Boolean).sort(),
              elements: [...new Set(cleanedData.map(row => row.Elements))].filter(Boolean).sort()
            };

            // Build random cascading path through the data
            let initCountries = [], initDomains = [], initComponents = [], initSubcomponents = [], initElements = [];
            let initAvailDomains = [], initAvailComponents = [], initAvailSubcomponents = [], initAvailElements = [];

            if (structure.countries.length > 0) {
              const randomCountry = structure.countries[Math.floor(Math.random() * structure.countries.length)];
              initCountries = [randomCountry];
              
              const countryData = cleanedData.filter(row => row.Country === randomCountry);
              initAvailDomains = [...new Set(countryData.map(row => row.Domain))].filter(Boolean).sort();
              
              if (initAvailDomains.length > 0) {
                const randomDomain = initAvailDomains[Math.floor(Math.random() * initAvailDomains.length)];
                initDomains = [randomDomain];
                
                const domainData = countryData.filter(row => row.Domain === randomDomain);
                initAvailComponents = [...new Set(domainData.map(row => row.Component))].filter(Boolean).sort();
                
                if (initAvailComponents.length > 0) {
                  const randomComponent = initAvailComponents[Math.floor(Math.random() * initAvailComponents.length)];
                  initComponents = [randomComponent];
                  
                  const componentData = domainData.filter(row => row.Component === randomComponent);
                  initAvailSubcomponents = [...new Set(componentData.map(row => row.Subcomponent))].filter(Boolean).sort();
                  
                  if (initAvailSubcomponents.length > 0) {
                    const randomSubcomponent = initAvailSubcomponents[Math.floor(Math.random() * initAvailSubcomponents.length)];
                    initSubcomponents = [randomSubcomponent];
                    
                    const subcompData = componentData.filter(row => row.Subcomponent === randomSubcomponent);
                    initAvailElements = [...new Set(subcompData.map(row => row.Elements))].filter(Boolean).sort();
                    initElements = initAvailElements; // Select all elements
                  }
                }
              }
            }

            // Set all state at once
            setData(cleanedData);
            setDataStructure(structure);
            setAvailableDomains(initAvailDomains);
            setAvailableComponents(initAvailComponents);
            setAvailableSubcomponents(initAvailSubcomponents);
            setAvailableElements(initAvailElements);
            setSelectedCountries(initCountries);
            setSelectedDomains(initDomains);
            setSelectedComponents(initComponents);
            setSelectedSubcomponents(initSubcomponents);
            setSelectedElements(initElements);
            setLoading(false);
            
            // Mark data as loaded AFTER state is set
            setTimeout(() => setDataLoaded(true), 50);
          },
          error: error => { console.error('Error parsing CSV:', error); setLoading(false); }
        });
      } catch (error) { console.error('Error loading data:', error); setLoading(false); }
    };
    loadData();
  }, []);

  // Cascading filter: Countries -> Domains (only run after initial load)
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    
    if (selectedCountries.length === 0) {
      setAvailableDomains([]);
      setSelectedDomains([]);
      return;
    }
    const countryData = data.filter(row => selectedCountries.includes(row.Country));
    const domains = [...new Set(countryData.map(row => row.Domain))].filter(Boolean).sort();
    setAvailableDomains(domains);
    setSelectedDomains(prev => prev.filter(d => domains.includes(d)));
  }, [selectedCountries, dataLoaded]);

  // Cascading filter: Domains -> Components
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    
    if (selectedCountries.length === 0 || selectedDomains.length === 0) {
      setAvailableComponents([]);
      setSelectedComponents([]);
      return;
    }
    const filtered = data.filter(row => selectedCountries.includes(row.Country) && selectedDomains.includes(row.Domain));
    const components = [...new Set(filtered.map(row => row.Component))].filter(Boolean).sort();
    setAvailableComponents(components);
    setSelectedComponents(prev => prev.filter(c => components.includes(c)));
  }, [selectedDomains, dataLoaded]);

  // Cascading filter: Components -> Subcomponents
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    
    if (selectedCountries.length === 0 || selectedDomains.length === 0 || selectedComponents.length === 0) {
      setAvailableSubcomponents([]);
      setSelectedSubcomponents([]);
      return;
    }
    const filtered = data.filter(row => 
      selectedCountries.includes(row.Country) && selectedDomains.includes(row.Domain) && selectedComponents.includes(row.Component)
    );
    const subcomponents = [...new Set(filtered.map(row => row.Subcomponent))].filter(Boolean).sort();
    setAvailableSubcomponents(subcomponents);
    setSelectedSubcomponents(prev => prev.filter(s => subcomponents.includes(s)));
  }, [selectedComponents, dataLoaded]);

  // Cascading filter: Subcomponents -> Elements
  useEffect(() => {
    if (!dataLoaded || data.length === 0) return;
    
    if (selectedCountries.length === 0 || selectedDomains.length === 0 || selectedComponents.length === 0 || selectedSubcomponents.length === 0) {
      setAvailableElements([]);
      setSelectedElements([]);
      return;
    }
    const filtered = data.filter(row => 
      selectedCountries.includes(row.Country) && selectedDomains.includes(row.Domain) && 
      selectedComponents.includes(row.Component) && selectedSubcomponents.includes(row.Subcomponent)
    );
    const elements = [...new Set(filtered.map(row => row.Elements))].filter(Boolean).sort();
    setAvailableElements(elements);
    setSelectedElements(prev => prev.filter(e => elements.includes(e)));
  }, [selectedSubcomponents, dataLoaded]);

  // Filter data for chart
  useEffect(() => {
    if (data.length === 0 || selectedCountries.length === 0 || selectedDomains.length === 0 || 
        selectedComponents.length === 0 || selectedSubcomponents.length === 0 || selectedElements.length === 0) {
      setFilteredData([]);
      return;
    }
    const filtered = data.filter(row =>
      selectedCountries.includes(row.Country) && selectedDomains.includes(row.Domain) &&
      selectedComponents.includes(row.Component) && selectedSubcomponents.includes(row.Subcomponent) &&
      selectedElements.includes(row.Elements)
    );
    setFilteredData(filtered);
  }, [data, selectedCountries, selectedDomains, selectedComponents, selectedSubcomponents, selectedElements]);

  // Render chart
  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) {
      if (chartRef.current) Plotly.purge(chartRef.current);
      return;
    }
    renderBarChart();
  }, [filteredData, showLegend]);

  const renderBarChart = () => {
    const elementScores = {};
    filteredData.forEach(row => {
      if (!elementScores[row.Elements]) elementScores[row.Elements] = { scores: [], definition: row.ElementDefinition };
      elementScores[row.Elements].scores.push(row.ElementScore);
    });

    const elements = Object.keys(elementScores);
    const scores = elements.map(el => elementScores[el].scores.reduce((a, b) => a + b, 0) / elementScores[el].scores.length);
    const definitions = elements.map(el => elementScores[el].definition);
    const colors = elements.map((_, idx) => colorPalette[idx % colorPalette.length]);

    const trace = {
      x: elements, y: scores, type: 'bar',
      marker: { color: colors, line: { color: 'white', width: 1 } },
      text: scores.map(s => `${s.toFixed(0)}%`),
      textposition: 'outside',
      textfont: { size: 11, color: '#374151' },
      hovertemplate: elements.map((el, idx) => `<b>${el}</b><br>Score: ${scores[idx].toFixed(1)}%<br><i>${definitions[idx] ? definitions[idx].substring(0, 100) + '...' : ''}</i><extra></extra>`)
    };

    const countryText = selectedCountries.length === 1 ? selectedCountries[0] : `${selectedCountries.length} Countries`;
    const subcompText = selectedSubcomponents.length === 1 ? selectedSubcomponents[0] : `${selectedSubcomponents.length} Subcomponents`;

    const layout = {
      title: { text: `${countryText} — ${subcompText}`, font: { size: 18, color: '#374151' }, x: 0.5 },
      xaxis: { title: { text: 'Elements', font: { size: 14, color: '#6B7280' } }, tickangle: -45, tickfont: { size: 10, color: '#6B7280' }, automargin: true },
      yaxis: { title: { text: 'Element Score (%)', font: { size: 14, color: '#6B7280' } }, range: [0, 110], tickfont: { size: 12, color: '#6B7280' }, showgrid: true, gridcolor: '#E5E7EB' },
      showlegend: false, hovermode: 'closest', plot_bgcolor: 'white', paper_bgcolor: 'white', margin: { l: 80, r: 40, t: 80, b: 150 }, bargap: 0.3
    };

    Plotly.newPlot(chartRef.current, [trace], layout, { responsive: true, displaylogo: false });
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const toggleData = () => setShowData(!showData);

  let tableData = [...filteredData];
  if (sortConfig.key) {
    tableData.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  if (loading) return <div className="loading-container"><div className="loading-message">Loading T1D Assessment Data...</div></div>;

  const hasAllSelections = selectedCountries.length > 0 && selectedDomains.length > 0 && 
    selectedComponents.length > 0 && selectedSubcomponents.length > 0 && selectedElements.length > 0;

  return (
    <div className="explorer-container" data-sidebar-open={isSidebarOpen.toString()} style={{ '--sidebar-width': isSidebarOpen ? '300px' : '40px', position: 'relative' }}>
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
        {isSidebarOpen && (
          <div className="sidebar-content">
            <div className="section">
              <h2 className="section-title">Country</h2>
              <SearchableMultiSelect options={dataStructure.countries} selected={selectedCountries}
                onToggle={(c) => setSelectedCountries(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                onSelectAll={() => setSelectedCountries(dataStructure.countries)} onDeselectAll={() => setSelectedCountries([])}
                placeholder="Select Countries" id="countries-select" />
              <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{selectedCountries.length} of {dataStructure.countries.length} selected</span>
            </div>

            <div className="section">
              <h2 className="section-title">Domain</h2>
              <SearchableMultiSelect options={availableDomains} selected={selectedDomains}
                onToggle={(d) => setSelectedDomains(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d])}
                onSelectAll={() => setSelectedDomains(availableDomains)} onDeselectAll={() => setSelectedDomains([])}
                placeholder={selectedCountries.length > 0 ? "Select Domains" : "Select Country first"} id="domains-select" disabled={selectedCountries.length === 0} />
              {selectedCountries.length > 0 && <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{selectedDomains.length} of {availableDomains.length} selected</span>}
            </div>

            <div className="section">
              <h2 className="section-title">Component</h2>
              <SearchableMultiSelect options={availableComponents} selected={selectedComponents}
                onToggle={(c) => setSelectedComponents(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c])}
                onSelectAll={() => setSelectedComponents(availableComponents)} onDeselectAll={() => setSelectedComponents([])}
                placeholder={selectedDomains.length > 0 ? "Select Components" : "Select Domain first"} id="components-select" disabled={selectedDomains.length === 0} />
              {selectedDomains.length > 0 && <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{selectedComponents.length} of {availableComponents.length} selected</span>}
            </div>

            <div className="section">
              <h2 className="section-title">Subcomponent</h2>
              <SearchableMultiSelect options={availableSubcomponents} selected={selectedSubcomponents}
                onToggle={(s) => setSelectedSubcomponents(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])}
                onSelectAll={() => setSelectedSubcomponents(availableSubcomponents)} onDeselectAll={() => setSelectedSubcomponents([])}
                placeholder={selectedComponents.length > 0 ? "Select Subcomponents" : "Select Component first"} id="subcomponents-select" disabled={selectedComponents.length === 0} />
              {selectedComponents.length > 0 && <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{selectedSubcomponents.length} of {availableSubcomponents.length} selected</span>}
            </div>

            <div className="section">
              <h2 className="section-title">Elements</h2>
              <SearchableMultiSelect options={availableElements} selected={selectedElements}
                onToggle={(e) => setSelectedElements(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e])}
                onSelectAll={() => setSelectedElements(availableElements)} onDeselectAll={() => setSelectedElements([])}
                placeholder={selectedSubcomponents.length > 0 ? "Select Elements" : "Select Subcomponent first"} id="elements-select" disabled={selectedSubcomponents.length === 0} />
              {selectedSubcomponents.length > 0 && <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{selectedElements.length} of {availableElements.length} selected</span>}
            </div>

            <div className="section">
              <label className="checkbox-label"><input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} /><span>Show Legend</span></label>
            </div>

            <div className="section">
              <button className="button button-secondary" onClick={toggleData} style={{ width: '100%' }}>{showData ? 'Hide Data Table' : 'Show Data Table'}</button>
            </div>
          </div>
        )}
      </div>

      <div className="main-content">
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="header-title" style={{ margin: 0, lineHeight: '1.3' }}>T1D Readiness Assessment</h1>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#6B7280' }}>Element Scores by Country and Component</p>
          </div>
          {hasAllSelections && (
            <button onClick={exportToPNG} title="Download chart as PNG"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              PNG
            </button>
          )}
        </div>

        <div className="content-area" ref={contentAreaRef}>
          {!hasAllSelections ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#6B7280', fontSize: '1.1rem' }}>
              <p style={{ marginBottom: '1rem' }}>Please select all required options to display the chart:</p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ color: selectedCountries.length > 0 ? '#10B981' : '#EF4444' }}>{selectedCountries.length > 0 ? '✓' : '○'} Country ({selectedCountries.length} selected)</li>
                <li style={{ color: selectedDomains.length > 0 ? '#10B981' : '#EF4444' }}>{selectedDomains.length > 0 ? '✓' : '○'} Domain ({selectedDomains.length} selected)</li>
                <li style={{ color: selectedComponents.length > 0 ? '#10B981' : '#EF4444' }}>{selectedComponents.length > 0 ? '✓' : '○'} Component ({selectedComponents.length} selected)</li>
                <li style={{ color: selectedSubcomponents.length > 0 ? '#10B981' : '#EF4444' }}>{selectedSubcomponents.length > 0 ? '✓' : '○'} Subcomponent ({selectedSubcomponents.length} selected)</li>
                <li style={{ color: selectedElements.length > 0 ? '#10B981' : '#EF4444' }}>{selectedElements.length > 0 ? '✓' : '○'} Elements ({selectedElements.length} selected)</li>
              </ul>
            </div>
          ) : <div ref={chartRef} style={{ width: '100%', height: '100%' }} />}
        </div>
      </div>

      {showData && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.9)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }} onClick={toggleData}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', maxWidth: '90vw', maxHeight: '80vh', width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Filtered Data ({filteredData.length} records)</h3>
              <button onClick={toggleData} style={{ backgroundColor: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6B7280' }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6' }}>
                  <tr>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Country')}>Country</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Domain')}>Domain</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Component')}>Component</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Subcomponent')}>Subcomponent</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('Elements')}>Element</th>
                    <th style={{ padding: '12px', border: '1px solid #D1D5DB', textAlign: 'left', cursor: 'pointer' }} onClick={() => handleSort('ElementScore')}>Score (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Country}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Domain}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Component}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Subcomponent}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.Elements}</td>
                      <td style={{ padding: '8px', border: '1px solid #E5E7EB' }}>{row.ElementScore}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      <Tooltip id="info-tooltip" place="right" />
    </div>
  );
};

export default Assessment;